import { themeSourceLabel } from "@/shared/vocabulary/classification-sources";
import { polarityLabel } from "@/shared/types/polarity";

import { formatDateTime, formatShortDate } from "@/shared/utils/format";
import type { Span } from "@/shared/utils/highlight";
/**
 * Rótulo de exibição de polaridade. Era uma union fixa de 4 valores —
 * mas o domínio de polaridade é configuração da instalação
 * (`core/polarity_domain.json`), servida por `GET /api/polarities`, não
 * lista de código. String aberta de propósito.
 */
export type Sentiment = string;

export type Mention = {
  /** `post_company_link.id` — o mesmo post visto por duas marcas são
   * menções diferentes, cada uma com sua classificação e score. */
  id: number;
  source: string;
  icon: string;
  title: string;
  excerpt: string;
  date: string;
  /** Rótulo de exibição — vem do domínio (`polarityLabel`). */
  sentiment: Sentiment;
  /** Valor de wire da polaridade (`POSITIVO`…). `null` = sem
   * classificação. */
  polarity: string | null;
  approach: string;
  /** Citação no título (2026-10-22): o título cita a marca? Só
   *  impressa/online. `null` = não se aplica (rede, TV/rádio, menção
   *  sem detecção): a tela não mostra o controle. O histórico já vem
   *  preenchido pelo servidor. */
  titleCitation: boolean | null;
  /** Valor de wire da abordagem (`protagonist`…). */
  approachValue: "protagonist" | "supporting" | "citation" | null;
  theme: string;
  /**
   * `null` quando a menção não tem nota — que é o caso NORMAL de post de
   * rede social (o `icena` vivo só existe pra imprensa; rede só fecha a
   * nota dentro de relatório).
   *
   * Era `number` com `?? 0` no mapeamento até 2026-08-12, e o zero
   * inventado vazava pra toda parte: puxava média pra baixo e, na lista
   * "menções que pedem contexto" (ordenada por CENA crescente), fazia
   * todo post de rede empatar em zero e ocupar o topo — a lista das
   * piores mostrava as sem nota.
   */
  cena: number | null;
  reach: string;
  publishedAt: string;
  /** Link da CLIPADORA (alvo do scraping). */
  link: string | null;
  /** Link do VEÍCULO — a tela pode oferecer os dois cliques. */
  originalLink: string | null;
  /** `press` | `network`. */
  mediaType: string;
  /** Valoração em reais — só imprensa tem; `null` em rede quer
   *  dizer "não se aplica", e `0` é valor legítimo. */
  adValue: number | null;
  /** Alcance (audiência) da planilha, só imprensa; `null` = não veio.
   *  Diferente de `reach`, que é o alcance de REDE já formatado (seguidores). */
  pressReach: number | null;
  clippingService: string;
  channel: string | null;
  /** Auditoria: quais termos da marca casaram — responde "por que isso
   * está aqui?" sem reprocessar. */
  foundTerms: string | null;
  escena: number | null;
  cenaComponents: Record<string, number>;
  classificationMethod: string | null;
  /** `imported` quando a polaridade / a abordagem veio da coluna da
   * planilha escolhida no envio (2026-10-08); `null` = do motor ou de
   * quem corrigiu. Tema e mensagem-chave levam a origem no próprio
   * `CatalogRef.source`. */
  polaritySource: string | null;
  approachSource: string | null;
  /** Por que a polaridade é esta (2026-10-20): com origem `term`, os
   * termos cadastrados que casaram, separados por "; ". */
  polarityJustification: string | null;
  /** Com origem `ai`: o número da PREMISSA que a IA citou, a mesma
   * numeração da aba Configuração de prompt. */
  polarityRulePosition: number | null;
  /** Com origem `ai`: a participação por entidade, em PT-BR, um trecho
   * por entidade separado por " | ". */
  approachJustification: string | null;
  brandName: string | null;
  brandId: number;
  followerCount: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  reactions: number | null;
  views: number | null;
  /** Sem coluna no schema hoje — a tela degrada. Volta quando o
   * `link_preview.py` (já colhido em `web_api/infrastructure/`) tiver
   * onde guardar o resultado. */
  previewImageUrl: string | null;
  /** O código da matéria NA CLIPADORA ("Código da Notícia" na Fábrica
   * — o "código empauta" que a galera de negócio confere contra a
   * planilha e o relatório anterior). `null` quando a clipadora não
   * manda. */
  nativeId: string | null;
  /** Quem publicou: nome, nick e link do perfil — só rede. */
  authorName: string | null;
  authorId: string | null;
  authorProfileUrl: string | null;
  /** As dimensões N:N da classificação. **Temas, mensagens-chave e
   * porta-vozes vêm na LISTAGEM** (temas desde 2026-09-21, com o
   * grupo-tema; os outros dois desde 2026-10-18), uma consulta por
   * página cada, e também no DETALHE (`GET /mentions/{id}`).
   *
   * **A edição manual depende delas**: um formulário montado sem os
   * valores atuais abriria vazio e apagaria o que a IA classificou. */
  themes: CatalogRef[];
  keyMessages: CatalogRef[];
  spokespersons: CatalogRef[];
  /** Os relatórios SALVOS em que esta menção entrou — "já saiu no
   * relatório X". Vazio = nunca foi apresentada. */
  reports: ReportRef[];
  /** As OUTRAS marcas que veem esta mesma publicação — a "duplicada"
   * da visão por marca. Vazio = só esta marca. */
  otherBrands: SiblingBrand[];
  /** Onde os termos da marca casam, sobre `title` e sobre `excerpt`
   * (que é o trecho na lista e a matéria inteira no detalhe — o
   * servidor calcula sobre o texto que mandou). */
  highlights: { title: Span[]; content: Span[] };
  /** O resumo feito pela IA e de onde veio; `null` = a menção não tem. */
  summary: string | null;
  summaryOrigin: SummaryOrigin | null;
  /** Descarte (soft-delete): `null` = na tela. A lista normal nunca
   * traz descartada; só a lista `discarded: true` e o detalhe. */
  discardedAt: string | null;
  discardedByName: string | null;
  discardReason: string | null;
  /** Revisão (2026-10-03): `null` = ainda não revisada. Marca da
   *  pessoa — só gente põe e só gente tira. */
  reviewedAt: string | null;
  reviewedByName: string | null;
  /** A última linha da trilha (2026-10-07): quem mexeu por último e
   *  quando. `null` = ninguém mexeu. O que mudou está no histórico do
   *  detalhe. */
  lastEdit: { at: string; byName: string | null } | null;
  /** Vínculo criado à mão (2026-10-03): `null` = veio da esteira. */
  linkedAt: string | null;
  linkedByName: string | null;
  /** A menção é de um CONCORRENTE monitorado. O editor usa pra buscar
   *  o catálogo dele em vez do da empresa. */
  isCompetitor: boolean;
  /** O nome da empresa DA MENÇÃO — o do concorrente, quando é dele. */
  companyName: string;
};

export type SummaryOrigin = "ingest" | "on_demand";

export type SiblingBrand = {
  mentionId: number;
  brandName: string;
  companyName: string;
  isCompetitor: boolean;
};

export type ApiSiblingBrand = {
  mention_id: number;
  brand_name: string;
  company_name: string;
  is_competitor: boolean;
};

/** Item de catálogo associado a uma menção — `id` pra salvar, `name`
 * pra exibir. `group_name` só vem em tema: o grupo-tema do catálogo. */
export type CatalogRef = {
  id: number;
  name: string;
  group_name?: string | null;
  /** Em tema: `publisher`/`vehicle` quando veio da regra de cadastro
   * (por publicador ou veículo), `imported` quando veio da coluna da
   * planilha, `term` quando um termo cadastrado casou e `ai` quando a IA
   * o escolheu. Em mensagem-chave: `imported` ou `ai`. `null` = histórico
   * sem registro de origem, ou valor posto à mão. */
  source?: string | null;
  /** Só em tema: com origem `term`, a expressão que casou; com `ai`, a
   * frase que a IA deu. Nos outros casos, nulo. */
  justification?: string | null;
  /** Só em tema: `manual` (alguém escolheu) ou `auto` (sugestão) no
   * tema em DESTAQUE da menção; nulo nos outros. No máximo um por
   * menção, e ele vem PRIMEIRO na lista. */
  highlight?: "manual" | "auto" | null;
};

/** Um relatório salvo em que a menção entrou. `name` é nulo nos
 * anteriores a 2026-08-26 — `reportRefLabel` deriva o rótulo da
 * janela, como a lista de relatórios faz. */
export type ReportRef = {
  id: number;
  name: string | null;
  windowStart: string;
  windowEnd: string;
};

export type ApiReportRef = {
  id: number;
  name: string | null;
  window_start: string;
  window_end: string;
};

/** O tema no detalhe da menção: grupo, nome e, quando ele entrou pela
 * regra de cadastro, por quê — senão a pessoa procura no texto um termo
 * que não está lá. Origem desconhecida não inventa rótulo. */
export function themeLabel(theme: CatalogRef): string {
  const name = theme.group_name ? `${theme.group_name} · ${theme.name}` : theme.name;
  const source = themeSourceLabel(theme.source);
  const notes = [theme.highlight ? "em destaque" : undefined, source].filter(Boolean);
  return notes.length ? `${name} (${notes.join(", ")})` : name;
}

/** Por que este tema é o destaque — o texto do tooltip da estrela. */
export function highlightTitle(theme: CatalogRef): string | undefined {
  if (theme.highlight === "manual") return "Subtema em destaque (escolhido)";
  if (theme.highlight === "auto") return "Subtema em destaque (sugestão automática)";
  return undefined;
}

/** O rótulo de um relatório na linha da menção: o nome, ou a janela
 * quando o relatório é anterior a ter nome. */
export function reportRefLabel(report: ReportRef): string {
  if (report.name) return report.name;
  return `Amostra de ${formatShortDate(new Date(report.windowStart))} a ${formatShortDate(new Date(report.windowEnd))}`;
}

/** O wire de `GET /api/mentions` e `/api/mentions/{id}` — ver
 * `web_api/infrastructure/http/routes/mentions.py`. `content` só vem no
 * detalhe; a listagem manda `content_excerpt`. */
export type ApiMention = {
  id: number;
  /** De quem é a menção. Diferente da empresa autenticada quando é de
   * um concorrente monitorado — nesse caso a lista marca visualmente. */
  company_id: string;
  company_name: string;
  is_competitor: boolean;
  brand_id: number;
  brand_name: string;
  title: string | null;
  content_excerpt: string | null;
  content?: string | null;
  channel: string | null;
  post_source: string;
  clipping_service: string;
  link: string | null;
  original_link: string | null;
  published_at: string;
  found_terms: string | null;
  /** Nome do domínio de polaridade da instalação (`POSITIVO`,
   * `FAVORAVEL`…) — o mesmo `core/polarity_domain.json` que esta UI
   * consome via `/api/polarities`. Sem union fixa: é configuração. */
  polarity: string | null;
  approach: "protagonist" | "supporting" | "citation" | null;
  classification_method: string | null;
  polarity_source?: string | null;
  approach_source?: string | null;
  polarity_justification?: string | null;
  polarity_rule_position?: number | null;
  approach_justification?: string | null;
  /** Só imprensa; `null`/ausente = não se aplica. */
  title_citation?: boolean | null;
  /** Só existe pra IMPRENSA — o score de rede depende de percentil sobre
   * janela móvel e mora no relatório. */
  icena: number | null;
  escena: number | null;
  score_components: Record<string, number>;
  likes: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  follower_count: number | null;
  ad_value?: number | null;
  /** Alcance (audiência) que a planilha de IMPRENSA informou; `null`/ausente
   *  = não veio (rede, template sem a coluna, post anterior). Beta. */
  reach?: number | null;
  native_id?: string | null;
  /** Quem publicou (2026-10-17) — só rede; `null` em imprensa e em
   *  post anterior ao nome. */
  author_name?: string | null;
  author_id?: string | null;
  author_profile_url?: string | null;
  /** Na listagem E no detalhe desde 2026-09-21. */
  themes?: CatalogRef[];
  /** Só no detalhe — a listagem não paga uma query por menção. */
  key_messages?: CatalogRef[];
  spokespersons?: CatalogRef[];
  reports?: ApiReportRef[];
  raw_post_id?: number | null;
  other_brands?: ApiSiblingBrand[];
  highlights?: { title: Span[]; content: Span[] };
  /** Resumo da IA (2026-10-31): gravado na ingestão (`ingest`) ou gerado
   *  sob pedido (`on_demand`); `null` = a menção não tem. */
  summary?: string | null;
  summary_origin?: SummaryOrigin | null;
  discarded_at?: string | null;
  discarded_by?: string | null;
  discarded_by_name?: string | null;
  discard_reason?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  reviewed_by_name?: string | null;
  last_edit?: {
    changed_at: string;
    changed_by: string;
    changed_by_name: string | null;
  } | null;
  linked_at?: string | null;
  linked_by?: string | null;
  linked_by_name?: string | null;
};

// "187,1 mil", "1,4 mi" — o compacto do pt-BR, o mesmo que a valoração
// já usava (`formatCurrencyCompact`). Era "187.1K"/"1.4M", com ponto e
// sufixo em inglês, o único número da tela fora do padrão (2026-10-13).
const compactCount = new Intl.NumberFormat("pt-BR", {
  notation: "compact",
  maximumFractionDigits: 1
});

export function formatReach(count: number | null): string {
  if (count === null) return "—";
  return compactCount.format(count);
}

export function influencerTier(count: number | null): string | null {
  if (count === null || count < 1_000) return null;
  if (count < 1_000_000) return "Influenciador";
  return "Celebridade";
}

// Abordagem é vocabulário interno FIXO de 3 valores
// (`elt/silver/application/press_scoring.py::classify_approach`), não
// configuração — mapa fixo é correto aqui. Polaridade não tem mapa: o
// rótulo vem do domínio servido pela API (`polarityLabel`).
const approaches: Record<NonNullable<ApiMention["approach"]>, string> = {
  protagonist: "Protagonista",
  supporting: "Coadjuvante",
  citation: "Citação"
};

/** O rótulo de tela de um valor de abordagem (`null` = sem abordagem). */
export function approachLabel(value: string | null): string {
  if (value === null) return "Sem abordagem";
  return approaches[value as keyof typeof approaches] ?? value;
}

export function toMention(item: ApiMention): Mention {
  // As três derivações de exibição (origem, iniciais do ícone, data)
  // vêm de `postDisplay`, que existe pra isso — `toMention` as
  // reescrevia inline, e era a cópia mais antiga das duas.
  const display = postDisplay(item);
  return {
    id: item.id,
    source: display.source,
    icon: display.icon,
    title: item.title ?? "Publicação sem título",
    excerpt: item.content ?? item.content_excerpt ?? "",
    date: display.date,
    sentiment: polarityLabel(item.polarity),
    polarity: item.polarity,
    approach: item.approach ? approaches[item.approach] : "Não classificada",
    approachValue: item.approach,
    // Os temas de verdade chegam no DETALHE (`themes` abaixo). Este
    // campo continua sendo o rótulo de ORIGEM que a lista mostra — a
    // listagem não traz temas, e inventar "sem tema" ali seria pior que
    // dizer de onde a publicação veio.
    theme: item.post_source === "network" ? "Redes sociais" : "Notícias",
    cena: item.icena,
    reach: formatReach(item.follower_count),
    publishedAt: item.published_at,
    link: item.link,
    originalLink: item.original_link,
    mediaType: item.post_source,
    adValue: item.ad_value ?? null,
    pressReach: item.reach ?? null,
    clippingService: item.clipping_service,
    channel: item.channel,
    foundTerms: item.found_terms,
    escena: item.escena,
    cenaComponents: item.score_components,
    classificationMethod: item.classification_method,
    polaritySource: item.polarity_source ?? null,
    approachSource: item.approach_source ?? null,
    polarityJustification: item.polarity_justification ?? null,
    polarityRulePosition: item.polarity_rule_position ?? null,
    approachJustification: item.approach_justification ?? null,
    brandName: item.brand_name,
    brandId: item.brand_id,
    followerCount: item.follower_count,
    likes: item.likes,
    comments: item.comments,
    shares: item.shares,
    reactions: item.reactions,
    views: item.views,
    previewImageUrl: null,
    nativeId: item.native_id ?? null,
    authorName: item.author_name ?? null,
    authorId: item.author_id ?? null,
    authorProfileUrl: item.author_profile_url ?? null,
    // `?? []` por segurança com servidor/fixture antigo: tema,
    // mensagem-chave, porta-voz e relatório vêm na listagem e no detalhe
    // (os dois do meio desde 2026-10-18).
    themes: item.themes ?? [],
    keyMessages: item.key_messages ?? [],
    spokespersons: item.spokespersons ?? [],
    reports: (item.reports ?? []).map(report => ({
      id: report.id,
      name: report.name,
      windowStart: report.window_start,
      windowEnd: report.window_end
    })),
    otherBrands: (item.other_brands ?? []).map(sibling => ({
      mentionId: sibling.mention_id,
      brandName: sibling.brand_name,
      companyName: sibling.company_name,
      isCompetitor: sibling.is_competitor
    })),
    highlights: item.highlights ?? { title: [], content: [] },
    summary: item.summary ?? null,
    summaryOrigin: item.summary_origin ?? null,
    discardedAt: item.discarded_at ?? null,
    discardedByName: item.discarded_by_name ?? item.discarded_by ?? null,
    discardReason: item.discard_reason ?? null,
    titleCitation: item.title_citation ?? null,
    reviewedAt: item.reviewed_at ?? null,
    reviewedByName: item.reviewed_by_name ?? item.reviewed_by ?? null,
    lastEdit: item.last_edit
      ? {
          at: item.last_edit.changed_at,
          byName: item.last_edit.changed_by_name ?? item.last_edit.changed_by
        }
      : null,
    linkedAt: item.linked_at ?? null,
    linkedByName: item.linked_by_name ?? item.linked_by ?? null,
    isCompetitor: item.is_competitor,
    companyName: item.company_name
  };
}

/* --- publicações únicas ------------------------------------------- */

/**
 * Uma marca encontrada dentro de uma publicação.
 *
 * `mentionId` é o `post_company_link.id` DELA — clicar numa marca abre
 * a menção daquela marca, não "a publicação": classificação e CENA são
 * por marca, e é isso que o detalhe mostra.
 */
export type PublicationBrand = {
  mentionId: number;
  companyId: string;
  companyName: string;
  /** Marca de um concorrente monitorado, não da própria empresa. */
  isCompetitor: boolean;
  brandId: number;
  brandName: string;
  polarity: string | null;
  /** Rótulo de exibição, do domínio servido pela API. */
  sentiment: Sentiment;
  cena: number | null;
};

export type ApiPublicationBrand = {
  mention_id: number;
  company_id: string;
  company_name: string;
  is_competitor: boolean;
  brand_id: number;
  brand_name: string;
  polarity: string | null;
  icena: number | null;
};

export type ApiPublication = {
  raw_post_id: number;
  title: string | null;
  content_excerpt: string | null;
  channel: string | null;
  post_source: string;
  clipping_service: string;
  link: string | null;
  original_link: string | null;
  published_at: string;
  brands: ApiPublicationBrand[];
  likes: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  follower_count: number | null;
  ad_value?: number | null;
  /** Alcance (audiência) que a planilha de IMPRENSA informou; `null`/ausente
   *  = não veio (rede, template sem a coluna, post anterior). Beta. */
  reach?: number | null;
  native_id?: string | null;
  /** Quem publicou (2026-10-17) — só rede; `null` em imprensa e em
   *  post anterior ao nome. */
  author_name?: string | null;
  author_id?: string | null;
  author_profile_url?: string | null;
};

/**
 * Uma publicação — um post físico, não um vínculo.
 *
 * **Sem `polarity`/`cena` no topo, de propósito**: são por marca (a
 * mesma matéria pode ser favorável pra uma e desfavorável pra outra) e
 * moram em `brands`. Um valor único aqui teria que escolher entre
 * vários e mentiria em silêncio — o backend também não manda.
 *
 * O engajamento é do POST, então aparece UMA vez: na visão por marca a
 * mesma curtida aparece N vezes e somar a coluna dá número inflado.
 */
export type Publication = {
  rawPostId: number;
  source: string;
  icon: string;
  title: string;
  excerpt: string;
  date: string;
  publishedAt: string;
  link: string | null;
  originalLink: string | null;
  mediaType: string;
  /** Valoração em reais — só imprensa tem; `null` em rede quer
   *  dizer "não se aplica", e `0` é valor legítimo. */
  adValue: number | null;
  /** Alcance (audiência) da planilha, só imprensa; `null` = não veio.
   *  Diferente de `reach`, que é o alcance de REDE já formatado (seguidores). */
  pressReach: number | null;
  clippingService: string;
  channel: string | null;
  theme: string;
  /** Ver `Mention.nativeId`. */
  nativeId: string | null;
  /** Quem publicou: nome, nick e link do perfil — só rede. */
  authorName: string | null;
  authorId: string | null;
  authorProfileUrl: string | null;
  reach: string;
  brands: PublicationBrand[];
  followerCount: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  reactions: number | null;
  views: number | null;
};

/**
 * As derivações de exibição que as duas visões compartilham (origem,
 * iniciais do ícone, data formatada). Extraído quando ganhou o segundo
 * chamador — antes morava inteiro dentro de `toMention`.
 */
function postDisplay(item: {
  channel: string | null;
  clipping_service: string;
  published_at: string;
}) {
  const source = item.channel ?? item.clipping_service;
  const words = source.split(/\s+/).filter(Boolean);
  const icon = words.slice(0, 2).map(word => word[0]?.toUpperCase()).join("");
  return {
    source,
    icon: icon || "M",
    date: formatDateTime(new Date(item.published_at))
  };
}

export function toPublication(item: ApiPublication): Publication {
  const display = postDisplay(item);
  return {
    rawPostId: item.raw_post_id,
    source: display.source,
    icon: display.icon,
    title: item.title ?? "Publicação sem título",
    excerpt: item.content_excerpt ?? "",
    date: display.date,
    publishedAt: item.published_at,
    link: item.link,
    originalLink: item.original_link,
    mediaType: item.post_source,
    adValue: item.ad_value ?? null,
    pressReach: item.reach ?? null,
    clippingService: item.clipping_service,
    channel: item.channel,
    theme: item.post_source === "network" ? "Redes sociais" : "Notícias",
    nativeId: item.native_id ?? null,
    authorName: item.author_name ?? null,
    authorId: item.author_id ?? null,
    authorProfileUrl: item.author_profile_url ?? null,
    reach: formatReach(item.follower_count),
    brands: item.brands.map(brand => ({
      mentionId: brand.mention_id,
      companyId: brand.company_id,
      companyName: brand.company_name,
      isCompetitor: brand.is_competitor,
      brandId: brand.brand_id,
      brandName: brand.brand_name,
      polarity: brand.polarity,
      sentiment: polarityLabel(brand.polarity),
      cena: brand.icena
    })),
    followerCount: item.follower_count,
    likes: item.likes,
    comments: item.comments,
    shares: item.shares,
    reactions: item.reactions,
    views: item.views
  };
}

/**
 * A menção vista como publicação de UMA marca só.
 *
 * É o que faz a lista ter um formato só: a visão por marca vira uma
 * publicação com exatamente uma entrada em `brands`, e o componente
 * renderiza `brands` sempre — sem um `if` de modo espalhado por toda a
 * linha. `companyId`/`companyName`/`isCompetitor` vêm do payload real
 * (a menção sabe de quem é desde que o escopo de concorrente entrou).
 *
 * **SEM CHAMADOR desde que foi escrita, e é decisão pendente** (anotado
 * em 2026-09-02, a pedido da dona). O objetivo acima — "a lista ter um
 * formato só" — nunca chegou a acontecer por aqui: a tela de menções
 * continuou com as três visões montando o próprio bloco, e em 2026-09-02
 * a duplicação foi desfeita por outro caminho, extraindo
 * `PublicationHeaderLine`/`PublicationTitle`/`PublicationTags` em
 * `MentionsPage`. Então ou esta função é ADOTADA (e as três visões
 * passam a renderizar `Publication` sempre, que é o desenho que ela
 * propõe), ou ela SAI. Hoje não é nenhum dos dois, e ficou de pé só
 * porque o site ainda está crescendo.
 */
export function mentionAsPublication(item: ApiMention): Publication {
  const display = postDisplay(item);
  return {
    rawPostId: item.id,
    source: display.source,
    icon: display.icon,
    title: item.title ?? "Publicação sem título",
    excerpt: item.content_excerpt ?? "",
    date: display.date,
    publishedAt: item.published_at,
    link: item.link,
    originalLink: item.original_link,
    mediaType: item.post_source,
    adValue: item.ad_value ?? null,
    pressReach: item.reach ?? null,
    clippingService: item.clipping_service,
    channel: item.channel,
    theme: item.post_source === "network" ? "Redes sociais" : "Notícias",
    nativeId: item.native_id ?? null,
    authorName: item.author_name ?? null,
    authorId: item.author_id ?? null,
    authorProfileUrl: item.author_profile_url ?? null,
    reach: formatReach(item.follower_count),
    brands: [
      {
        mentionId: item.id,
        companyId: item.company_id,
        companyName: item.company_name,
        isCompetitor: item.is_competitor,
        brandId: item.brand_id,
        brandName: item.brand_name,
        polarity: item.polarity,
        sentiment: polarityLabel(item.polarity),
        cena: item.icena
      }
    ],
    followerCount: item.follower_count,
    likes: item.likes,
    comments: item.comments,
    shares: item.shares,
    reactions: item.reactions,
    views: item.views
  };
}

/**
 * O rótulo de quem publicou: "Nome (@nick)", só o nome, só o nick, ou
 * `null` quando o post não tem autor (imprensa, ou rede sem a coluna
 * mapeada). O nick ganha `@` só quando ainda não o tem — a Stilingue
 * manda "Autor ID" sem ele, o legado às vezes com.
 */
export function publisherLabel(item: {
  authorName: string | null;
  authorId: string | null;
}): string | null {
  const name = item.authorName?.trim() || null;
  const rawNick = item.authorId?.trim() || null;
  const nick = rawNick ? (rawNick.startsWith("@") ? rawNick : `@${rawNick}`) : null;
  if (name && nick) return `${name} (${nick})`;
  return name ?? nick;
}

/** O rótulo acima pronto pra colar depois da fonte: " · Nome (@nick)". */
export function publisherSuffix(item: {
  authorName: string | null;
  authorId: string | null;
}): string {
  const label = publisherLabel(item);
  return label ? ` · ${label}` : "";
}
